import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Divider } from 'antd'
import { Modal } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import styled from 'styled-components'
import { NeedItem, useNeedDelete } from 'hooks/useNeeds'

type DeleteNeedModalProps = {
  isModalVisible: boolean
  onClose: () => void
  need: NeedItem | null
  preferredPeopleNominative?: string
}

export const DeleteNeedModal: FC<DeleteNeedModalProps> = ({
  isModalVisible,
  onClose,
  need,
  preferredPeopleNominative,
}) => {
  const { t } = useTranslation()
  const { deleteNeed, isDeleting } = useNeedDelete(onClose)

  const onConfirm = async () => {
    if (!need) return
    await deleteNeed(need.id)
  }

  return (
    <StyledModal
      localisation={{
        title: t('skillsAndNeeds.deleteNeedModalTitle'),
        confirmText: t('skillsAndNeeds.deleteNeedConfirm'),
        cancelText: t('general.buttons.cancel'),
      }}
      onConfirm={onConfirm}
      onCancel={onClose}
      isModalVisible={isModalVisible}
      closeButton
      closeOnConfirm={false}
      noPadding
      confirmButtonProps={{ isLoading: isDeleting, variant: 'danger' }}
    >
      <Divider style={{ margin: '0' }} />
      <StyledContent>
        <Text level={1} weight="medium">
          {t('skillsAndNeeds.deleteNeedBody')}
        </Text>
        <StyledWarningBlock>
          <Text level={1}>{t('skillsAndNeeds.deleteNeedWarning')}</Text>
          <StyledList>
            <li>
              <Text level={1}>
                {t('skillsAndNeeds.deleteNeedProfilesItem', {
                  serviceUsers: preferredPeopleNominative,
                })}
              </Text>
            </li>
          </StyledList>
        </StyledWarningBlock>
      </StyledContent>
    </StyledModal>
  )
}

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.neutral.color8};
`

const StyledWarningBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: -4px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const StyledList = styled.ul`
  margin: 0;
  padding-left: 20px;
`

const StyledModal = styled(Modal)`
  min-width: 600px;

  .ant-modal-content {
    padding-top: 20px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
  }

  .ant-modal-header {
    padding-bottom: 4px !important;
  }

  .ant-modal-title {
    font-size: 22px !important;
    font-weight: 500 !important;
    text-transform: none !important;
    color: ${({ theme }) => theme.color.neutral.color3} !important;
  }

  .ant-modal-footer {
    margin-top: 0 !important;
    padding-top: 16px !important;
    padding-bottom: 16px !important;
  }
`

export default DeleteNeedModal

